import React from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Eye, Download, Trash2, QrCode, Stamp, Shield, XCircle, Printer, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getFileTypeInfo, canPreviewInline } from './constants';

const DocumentRow = ({ doc, categories, onView, onDownload, onPrint, onDelete, formatFileSize, formatDate, getStatusBadge, onPublish, publishingDoc, printingDocId }) => {
  const fileInfo = getFileTypeInfo(doc.original_filename || doc.filename, doc.mime_type);
  const FileIcon = fileInfo.icon;
  const previewable = canPreviewInline(doc.original_filename || doc.filename, doc.mime_type);

  return (
    <div className="group flex items-center gap-3 px-4 py-3 hover:bg-gray-50/80 transition-colors border-b border-gray-100 last:border-0" data-testid={`doc-row-${doc.id}`}>
      {/* File type icon */}
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border", fileInfo.bg, fileInfo.borderColor)}>
        <FileIcon className={cn("w-5 h-5", fileInfo.color)} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900 truncate">{doc.original_filename || doc.nom}</span>
          {getStatusBadge(doc.statut)}
          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 border", fileInfo.borderColor, fileInfo.color)}>
            {fileInfo.label}
          </Badge>
          {/* Registry status badge */}
          {doc.registry_status === 'valid' && (
            <Badge className="text-[10px] px-1.5 py-0 bg-emerald-700 text-white border border-emerald-900 gap-0.5 font-semibold" data-testid={`registry-badge-${doc.id}`}>
              <Shield className="w-2.5 h-2.5" /> Registre
            </Badge>
          )}
          {doc.registry_status === 'revoked' && (
            <Badge className="text-[10px] px-1.5 py-0 bg-red-700 text-white border border-red-900 gap-0.5 font-semibold">
              <XCircle className="w-2.5 h-2.5" /> Revoque
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
          <span>{categories.find(c => c.value === doc.categorie)?.label || doc.categorie}</span>
          <span className="text-gray-300">|</span>
          <span>{formatFileSize(doc.file_size)}</span>
          <span className="text-gray-300">|</span>
          <span>{formatDate(doc.uploaded_at)}</span>
          {(doc.user_prenom || doc.user_nom) && (
            <>
              <span className="text-gray-300">|</span>
              <span className="font-medium text-gray-600">
                {[doc.user_prenom, doc.user_nom].filter(Boolean).join(' ')}
              </span>
            </>
          )}
          {doc.registry_id && (
            <>
              <span className="text-gray-300">|</span>
              <span className="font-mono text-emerald-600 text-[10px]">{doc.registry_id}</span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {/* Publish to Registry button */}
        {!doc.registry_id && onPublish && (
          <Button
            variant="ghost" size="icon"
            onClick={() => onPublish(doc)}
            className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            title="Publier au registre"
            disabled={publishingDoc === doc.id}
            data-testid={`publish-btn-${doc.id}`}
          >
            <Stamp className="w-4 h-4" />
          </Button>
        )}
        {doc.registry_id && (
          <a
            href={doc.registry_public_url || `/verify/registry/${doc.registry_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 w-8 flex items-center justify-center rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            title="Voir sur le registre"
            data-testid={`registry-link-${doc.id}`}
          >
            <QrCode className="w-4 h-4" />
          </a>
        )}
        <Button
          variant="ghost" size="icon"
          onClick={() => onView(doc)}
          className={cn("h-8 w-8", previewable ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50" : "text-gray-500 hover:text-gray-700")}
          title={previewable ? "Prévisualiser" : "Ouvrir"}
          data-testid={`view-btn-${doc.id}`}
        >
          <Eye className="w-4 h-4" />
        </Button>
        {onPrint && (
          <Button
            variant="ghost" size="icon"
            onClick={() => onPrint(doc)}
            className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            title="Imprimer"
            disabled={printingDocId === doc.id}
            data-testid={`print-btn-${doc.id}`}
          >
            {printingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
          </Button>
        )}
        <Button
          variant="ghost" size="icon"
          onClick={() => onDownload(doc)}
          className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          title="Télécharger"
          data-testid={`download-btn-${doc.id}`}
        >
          <Download className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost" size="icon"
          onClick={() => onDelete(doc.id)}
          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
          title="Supprimer"
          data-testid={`delete-btn-${doc.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// === DOCUMENT CARD (Grid View) ===

export default DocumentRow;
